import { Constraint } from './types';

export interface FamilyParameter {
    id: string;
    name: string;
    type: 'Length' | 'Number' | 'Text' | 'YesNo' | 'Material' | 'FamilyType';
    isInstance: boolean;
    isShared: boolean;
    groupName?: string;
    formula?: string;
    value?: any;
}

export interface FamilyType {
    id: string;
    name: string;
    parameterValues: Record<string, any>; // Parameter ID -> Value
}

export interface FamilyDocument {
    id: string;
    name: string;
    category: string;
    isShared: boolean;
    parameters: FamilyParameter[];
    types: FamilyType[];
    geometry: FamilyGeometry[];
    constraints: Constraint[];
    nestedFamilies: NestedFamily[];
    symbolicLines: SymbolicLine[];
    maskingRegions: MaskingRegion[];
}

export interface FamilyGeometry {
    id: string;
    type: 'Extrusion' | 'Blend' | 'Revolve' | 'Sweep' | 'SweptBlend' | 'Void';
    profileSketch: SketchProfile;
    parameters: Record<string, string>; // e.g., { height: 'paramId' }
}

export interface SketchProfile {
    lines: { start: [number, number]; end: [number, number] }[];
    arcs: { center: [number, number]; radius: number; startAngle: number; endAngle: number }[];
}

export interface NestedFamily {
    id: string;
    familyId: string;
    position: [number, number, number];
    isShared: boolean;
}

export interface SymbolicLine {
    id: string;
    start: [number, number];
    end: [number, number];
    lineStyle: string;
    visibleIn: 'Plan' | 'Front' | 'All';
}

export interface MaskingRegion {
    id: string;
    boundary: [number, number][];
    visibleIn: 'Plan' | 'Front' | 'All';
}

export class FamilyEditor {

    private activeFamily: FamilyDocument | null = null;
    private isInFamilyMode: boolean = false;

    /**
     * Create a new family document.
     */
    createFamily(name: string, category: string): FamilyDocument {
        const family: FamilyDocument = {
            id: crypto.randomUUID(),
            name,
            category,
            isShared: false,
            parameters: [],
            types: [],
            geometry: [],
            constraints: [],
            nestedFamilies: [],
            symbolicLines: [],
            maskingRegions: []
        };

        // Add default type
        family.types.push({
            id: crypto.randomUUID(),
            name: 'Default',
            parameterValues: {}
        });

        return family;
    }

    /**
     * Enter family editing mode.
     */
    enterFamilyMode(family: FamilyDocument) {
        this.activeFamily = family;
        this.isInFamilyMode = true;
    }

    /**
     * Exit family editing mode.
     */
    exitFamilyMode(): FamilyDocument | null {
        const family = this.activeFamily;
        this.activeFamily = null;
        this.isInFamilyMode = false;
        return family;
    }

    /**
     * Check if in family editing mode.
     */
    isEditing(): boolean {
        return this.isInFamilyMode;
    }

    /**
     * Add a parameter to the active family.
     */
    addParameter(name: string, type: FamilyParameter['type'], isInstance: boolean = false): FamilyParameter | null {
        if (!this.activeFamily) return null;

        const param: FamilyParameter = {
            id: crypto.randomUUID(),
            name,
            type,
            isInstance,
            isShared: false
        };

        this.activeFamily.parameters.push(param);
        return param;
    }

    /**
     * Add a formula to a parameter.
     */
    setParameterFormula(parameterId: string, formula: string): boolean {
        if (!this.activeFamily) return false;

        const param = this.activeFamily.parameters.find(p => p.id === parameterId);
        if (param) {
            param.formula = formula;
            return true;
        }
        return false;
    }

    /**
     * Create a new family type.
     */
    createType(name: string): FamilyType | null {
        if (!this.activeFamily) return null;

        const type: FamilyType = {
            id: crypto.randomUUID(),
            name,
            parameterValues: {}
        };

        this.activeFamily.types.push(type);
        return type;
    }

    /**
     * Set parameter value for a type.
     */
    setTypeParameterValue(typeId: string, parameterId: string, value: any): boolean {
        if (!this.activeFamily) return false;

        const type = this.activeFamily.types.find(t => t.id === typeId);
        if (type) {
            type.parameterValues[parameterId] = value;
            return true;
        }
        return false;
    }

    /**
     * Add a symbolic line (2D representation).
     */
    addSymbolicLine(start: [number, number], end: [number, number], lineStyle: string = 'Object', visibleIn: SymbolicLine['visibleIn'] = 'Plan'): SymbolicLine | null {
        if (!this.activeFamily) return null;

        const line: SymbolicLine = {
            id: crypto.randomUUID(),
            start,
            end,
            lineStyle,
            visibleIn
        };

        this.activeFamily.symbolicLines.push(line);
        return line;
    }

    /**
     * Add a nested family instance.
     */
    addNestedFamily(familyId: string, position: [number, number, number], isShared: boolean = false): NestedFamily | null {
        if (!this.activeFamily) return null;

        const nested: NestedFamily = {
            id: crypto.randomUUID(),
            familyId,
            position,
            isShared
        };

        this.activeFamily.nestedFamilies.push(nested);
        return nested;
    }

    /**
     * Evaluate formulas and resolve parameter values for a type.
     */
    resolveTypeValues(typeId: string): Record<string, any> {
        if (!this.activeFamily) return {};

        const type = this.activeFamily.types.find(t => t.id === typeId);
        if (!type) return {};

        const resolved: Record<string, any> = { ...type.parameterValues };

        // Evaluate formulas
        this.activeFamily.parameters.forEach(param => {
            if (param.formula) {
                resolved[param.id] = this.evaluateFormula(param.formula, resolved);
            }
        });

        return resolved;
    }

    private evaluateFormula(formula: string, values: Record<string, any>): number {
        let expr = formula;

        // Replace parameter names with values
        for (const [id, value] of Object.entries(values)) {
            const param = this.activeFamily?.parameters.find(p => p.id === id);
            if (param) {
                expr = expr.replace(new RegExp(param.name, 'g'), String(value || 0));
            }
        }

        try {
            return Function(`"use strict"; return (${expr})`)();
        } catch {
            return 0;
        }
    }
}
